import {
	createActionsHook,
	createContainer,
	createStateHook,
	createStore,
} from 'react-sweet-state';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { FormRenderMode } from '../../common/types/render-mode';
import {
	buildDefaultFormFieldValidators,
	buildSecurityCriticalValidators,
} from '../../common/utils/validators/constants';
import { actions } from './actions';
import { formFieldSelector } from './selectors';
import { attachmentConfigurationSelector } from './selectors/attachment-configuration';
import { currentValueSelector } from './selectors/current-value';
import { currentValuesSelector } from './selectors/current-values';
import { fieldCustomErrorSelector } from './selectors/field-custom-error';
import { fieldInvalidSelector } from './selectors/field-invalid';
import { fieldRequiredSelector } from './selectors/field-required';
import { fieldValidationErrorsSelector } from './selectors/field-validation-errors';
import { fileUploadProgressSelector } from './selectors/file-upload';
import { hasModifiedValuesSelector } from './selectors/has-modified-values';
import { lifecycleHandlersSelector } from './selectors/lifecycle-handlers';
import { renderModeSelector } from './selectors/render-mode';
import { renderersSelector } from './selectors/renderers';
import { totalFormAttachmentsSelector } from './selectors/total-form-attachments';
import type { FormRendererState, PublicActions, PublicState } from './types';

type FormRendererActions = typeof actions;

const FormRendererStore = createStore<FormRendererState, FormRendererActions>({
	initialState: {
		asyncFields: {},
		fieldCustomErrors: {},
		formFields: {},
		formSections: [],
		lifecycleHandlers: {},
		modifiedValues: {},
		renderers: {},
		renderMode: FormRenderMode.VIEW,
		fieldVisibility: {
			fieldSet: [],
		},
		fieldValidation: {},
		defaultValidators: buildDefaultFormFieldValidators(),
		securityCriticalValidators: buildSecurityCriticalValidators(),
		formId: '',
	},
	actions,
	name: 'FormRendererStore',
});

export const useFormRendererState: import('react-sweet-state').HookStateFunction<
	FormRendererState,
	void
> = createStateHook(FormRendererStore);

export const usePublicFormRendererState: () => PublicState = () => {
	const fileUploadProgress = useFileUploadProgress();
	const hasModifiedValues = useHasModifiedValues();
	return {
		isFileUploadInProgress: fileUploadProgress,
		hasModifiedValues,
	};
};

export const useFormRenderMode: import('react-sweet-state').HookStateFunction<
	FormRenderMode,
	void
> = createStateHook(FormRendererStore, {
	selector: renderModeSelector,
});

export const useFormField: import('react-sweet-state').HookStateFunction<
	import('../..').FormField,
	import('./selectors/form-field').FormFieldSelectorProps
> = createStateHook(FormRendererStore, {
	selector: formFieldSelector,
});

export const useFormRendererActions: import('react-sweet-state').HookActionsFunction<
	import('react-sweet-state').BoundActions<
		FormRendererState,
		{
			clearModifiedValues: () => import('react-sweet-state').Action<FormRendererState>;
			getFieldIdsFailingValidation: (
				validationTargets?: import('../..').ValidationTargets,
				formFields?: import('../..').FormFields,
				createAnalyticsEvent?: import('@atlaskit/analytics-next/types').CreateUIAnalyticsEvent,
			) => import('react-sweet-state').Action<FormRendererState, void, import('../..').FieldId[]>;
			getVisibleFieldValues: () => import('react-sweet-state').Action<
				FormRendererState,
				void,
				import('../../common/types/form/form-fields/field-values').FieldValuesPartial
			>;
			getFieldValues: () => import('react-sweet-state').Action<
				FormRendererState,
				void,
				import('../..').FieldValues
			>;
			setFieldCustomErrors: (
				fieldCustomErrors: import('../..').FieldCustomErrors,
			) => import('react-sweet-state').Action<FormRendererState>;
			setFormRendererProps: (
				asyncFields: import('../..').AsyncFields,
				form: import('../..').Form,
				lifecycleHandlers: import('../..').LifecycleHandlers,
				renderers: import('../..').Renderers,
				renderMode: FormRenderMode,
				fieldVisibility: import('../../common/types/form').FormFieldVisibility,
				fieldValidation: import('../..').FieldValidation,
				defaultValidators: import('../..').Validators,
				attachmentConfiguration?: import('../..').AttachmentConfiguration,
				securityCriticalValidators?: import('../..').Validators,
			) => import('react-sweet-state').Action<FormRendererState>;
			updateModifiedValue: (
				fieldId: import('../..').FieldId,
				modifiedFieldValue: import('../../common/types/form/form-fields/field-values').ModifiedFieldValue,
			) => import('react-sweet-state').Action<FormRendererState>;
		}
	>
> = createActionsHook(FormRendererStore);

export const usePublicFormRendererActions: () => PublicActions = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		clearModifiedValues,
		getFieldIdsFailingValidation,
		getFieldValues,
		setFieldCustomErrors,
		updateModifiedValue,
		getVisibleFieldValues,
	} = useFormRendererActions();
	return {
		clearModifiedValues,
		getFieldIdsFailingValidation: (validationTargets, formFields) =>
			getFieldIdsFailingValidation(validationTargets, formFields, createAnalyticsEvent),
		getFieldValues,
		setFieldCustomErrors,
		updateModifiedValue,
		getVisibleFieldValues,
	};
};

export const FormRendererContainer: import('react-sweet-state').OverrideContainerComponent<unknown> =
	createContainer<FormRendererState, FormRendererActions>(FormRendererStore);

export const useAttachmentConfiguration: import('react-sweet-state').HookStateFunction<
	import('../..').AttachmentConfiguration | undefined,
	void
> = createStateHook(FormRendererStore, {
	selector: attachmentConfigurationSelector,
});

export const useCurrentValues: import('react-sweet-state').HookStateFunction<
	import('../..').FieldValues,
	void
> = createStateHook(FormRendererStore, {
	selector: currentValuesSelector,
});

export const useCurrentValue: import('react-sweet-state').HookStateFunction<
	| import('../..').AdfValue
	| import('../..').DateTimeValue
	| import('../..').DateValue
	| import('../..').FilesValue
	| import('../..').LocalTimeValue
	| import('../..').NumberValue
	| import('../..').OptionsValue
	| import('../..').TextValue
	| undefined,
	import('./selectors/current-value').CombinedFieldSelectorProps
> = createStateHook(FormRendererStore, {
	selector: currentValueSelector,
});

export const useFieldCustomError: import('react-sweet-state').HookStateFunction<
	string | undefined,
	import('./selectors/field-custom-error').FieldCustomErrorSelectorProps
> = createStateHook(FormRendererStore, {
	selector: fieldCustomErrorSelector,
});

/**
 * This hook has been updated to return if the field has validation or custom errors.
 */
export const useFieldInvalid: import('react-sweet-state').HookStateFunction<
	boolean,
	import('./selectors/field-invalid').FieldInvalidSelectorProps
> = createStateHook(FormRendererStore, {
	selector: fieldInvalidSelector,
});

export const useFieldRequired: import('react-sweet-state').HookStateFunction<
	boolean,
	import('./selectors/field-required').FieldRequiredSelectorProps
> = createStateHook(FormRendererStore, {
	selector: fieldRequiredSelector,
});

export const useFieldValidationErrors: import('react-sweet-state').HookStateFunction<
	import('../../common/types/field-validation').ValidationError[],
	import('./selectors/field-validation-errors').FieldValidationErrorsSelectorProps
> = createStateHook(FormRendererStore, {
	selector: fieldValidationErrorsSelector,
});

export const useFileUploadProgress: import('react-sweet-state').HookStateFunction<boolean, void> =
	createStateHook(FormRendererStore, {
		selector: fileUploadProgressSelector,
	});

export const useLifecycleHandlers: import('react-sweet-state').HookStateFunction<
	import('../..').LifecycleHandlers,
	void
> = createStateHook(FormRendererStore, {
	selector: lifecycleHandlersSelector,
});

export const useHasModifiedValues: import('react-sweet-state').HookStateFunction<boolean, void> =
	createStateHook(FormRendererStore, {
		selector: hasModifiedValuesSelector,
	});

export const useRenderers: import('react-sweet-state').HookStateFunction<
	import('../..').Renderers,
	void
> = createStateHook(FormRendererStore, {
	selector: renderersSelector,
});

export const useTotalFormAttachments: import('react-sweet-state').HookStateFunction<number, void> =
	createStateHook(FormRendererStore, {
		selector: totalFormAttachmentsSelector,
	});
